import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, X, TrendingUp } from "lucide-react";

const THRESHOLD = 0.8;  // show at 80% of any cap
const DISMISS_KEY = "bl_upgrade_nudge_dismissed";

/**
 * UpgradeNudge — dismissible banner that appears when the current org is
 * approaching any plan cap (users/invoices/customers/vendors). Fetches
 * /api/billing/subscription, reads the usage object, and surfaces the
 * highest-pressure resource.
 *
 * Props:
 *   resource — optional hint ('invoices' | 'customers' | 'vendors' | 'users')
 *              to focus the banner on that resource when multiple are over
 *              the threshold. Defaults to "any" (show the most pressing).
 */
export default function UpgradeNudge({ resource }) {
    const [pressure, setPressure] = useState(null);  // {key, used, limit, pct}
    const [plan, setPlan] = useState("free");
    const [dismissed, setDismissed] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        api.get("/billing/subscription")
            .then(({ data }) => {
                if (cancelled) return;
                setPlan(data.plan);
                const usage = data.usage || {};
                // Build candidate list: only resources with a numeric limit > 0
                const candidates = Object.entries(usage)
                    .map(([k, v]) => {
                        if (!v || v.limit == null || v.limit <= 0) return null;
                        return { key: k, used: v.used, limit: v.limit, pct: v.used / v.limit };
                    })
                    .filter(Boolean)
                    .filter((c) => c.pct >= THRESHOLD);
                if (candidates.length === 0) { setPressure(null); return; }
                // When the parent page passes a resource hint, show the nudge ONLY
                // for that resource (keeps the banner contextually relevant). When no
                // hint is passed, surface the most-pressure resource.
                if (resource) {
                    const focused = candidates.find((c) => c.key === resource);
                    setPressure(focused || null);
                    return;
                }
                setPressure(candidates.sort((a, b) => b.pct - a.pct)[0]);
            })
            .catch(() => setPressure(null));
        return () => { cancelled = true; };
    }, [resource]);

    useEffect(() => {
        if (!pressure) return;
        // Reset the dismiss once the user either upgrades or crosses a new threshold.
        const stored = localStorage.getItem(DISMISS_KEY);
        if (stored) {
            try {
                const { key, pct } = JSON.parse(stored);
                // Re-show if they've used more since the last dismissal.
                if (key === pressure.key && pressure.pct <= pct + 0.05) {
                    setDismissed(true);
                }
            } catch (e) { /* ignore */ }
        }
    }, [pressure]);

    if (!pressure || dismissed) return null;
    if (plan === "team") return null;  // already on top plan

    const nextPlan = plan === "free" ? "Pro" : "Team";
    const pctRounded = Math.round(pressure.pct * 100);
    const isAtCap = pressure.used >= pressure.limit;
    const label = {
        invoices: "invoices", customers: "customers", vendors: "vendors", users: "users",
    }[pressure.key] || pressure.key;

    const onUpgrade = () => {
        // Deep link to the Billing page with the next plan pre-selected.
        const target = plan === "free" ? "pro" : "team";
        navigate(`/app/billing?select=${target}`);
    };

    const onDismiss = () => {
        setDismissed(true);
        localStorage.setItem(DISMISS_KEY, JSON.stringify({ key: pressure.key, pct: pressure.pct }));
    };

    return (
        <div
            className="mb-6 rounded-2xl border border-secondary/40 bg-gradient-to-r from-secondary/10 via-white to-ocean-50 px-5 py-4 flex items-center gap-4 shadow-sm"
            data-testid="upgrade-nudge"
            data-nudge-key={pressure.key}
        >
            <div className="h-10 w-10 rounded-xl bg-secondary/15 grid place-items-center shrink-0">
                {isAtCap ? <TrendingUp className="h-5 w-5 text-coral-dark" /> : <Sparkles className="h-5 w-5 text-secondary-dark" />}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-ocean-700">
                    {isAtCap
                        ? `You've hit your ${plan === "free" ? "Free" : "Pro"}-plan ${label} cap`
                        : `You're at ${pctRounded}% of your ${label} cap`}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                    {pressure.used} of {pressure.limit} {label} used. Upgrade to <span className="font-semibold text-ocean-700">{nextPlan}</span> for {nextPlan === "Team" ? "10 businesses + 25 users" : "unlimited invoices, customers & vendors"}.
                </div>
            </div>
            <Button
                onClick={onUpgrade}
                className="bg-ocean-700 hover:bg-ocean-700/90 text-white rounded-xl whitespace-nowrap"
                data-testid="upgrade-nudge-cta"
            >
                Upgrade to {nextPlan}
            </Button>
            <button
                type="button"
                onClick={onDismiss}
                className="h-8 w-8 grid place-items-center rounded-xl text-muted-foreground hover:bg-white"
                data-testid="upgrade-nudge-dismiss"
                title="Dismiss"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    );
}
